/** Rules for replacing a token allowance. Kept free of wallet imports so they can be checked alone. */

/**
 * Tokens whose approve reverts when it replaces a non-zero allowance, so the allowance goes to 0 first.
 * Checked on forks on 17 Oct 2026: approve(1) then approve(2) reverts for both.
 */
const ZERO_FIRST_TOKENS: Record<number, readonly string[]> = {
  1: ["0xdac17f958d2ee523a2206206994597c13d831ec7"], // USDT, Ethereum
  11155111: ["0x7169d38820dfd117c3fa1f22a697dba58d90ba06"], // test USDT, Sepolia
};

export function needsZeroFirst(chainId: number, token: string): boolean {
  const listed = ZERO_FIRST_TOKENS[chainId] || [];
  return listed.includes(String(token || "").toLowerCase());
}

/**
 * True only when the node says the call itself would revert. Missing gas money, a wallet that does
 * not forward eth_estimateGas, or a flaky RPC are not a sign the token wants a zero allowance first.
 */
export function isRevertError(error: unknown): boolean {
  const seen = new Set<unknown>();
  const queue: unknown[] = [error];
  while (queue.length) {
    const item = queue.shift();
    if (!item || typeof item !== "object" || seen.has(item)) {
      if (typeof item === "string" && /revert/i.test(item)) return true;
      continue;
    }
    seen.add(item);
    const rec = item as { code?: unknown; message?: unknown; data?: unknown; cause?: unknown; error?: unknown };
    if (rec.code === 3) return true;
    if (typeof rec.message === "string" && /revert/i.test(rec.message)) return true;
    queue.push(rec.data, rec.cause, rec.error);
    if (rec.data && typeof rec.data === "object") {
      queue.push((rec.data as { originalError?: unknown }).originalError);
    }
  }
  return false;
}

export const RESET_WARNING =
  "This token needs the allowance set to 0 first. Your other subscriptions on this wallet wait until the new approval confirms.";

/** What the customer reads when the allowance is already 0 and the new approval did not go through. */
export function afterResetError(cause: unknown): Error {
  const reason = cause instanceof Error && cause.message ? cause.message : "The approval did not go through";
  return new Error(
    `${reason}. The token allowance is 0 now, so subscriptions paid from this wallet cannot renew until you approve again. Retry to approve.`
  );
}

/**
 * Whether the allowance reached `need` after the wallet reported a failed or expired approve.
 * WalletConnect requests expire while the customer may still confirm on the phone, so the
 * approve can land anyway. A plain rejection gets one read; anything else is watched for `waitMs`.
 */
export async function allowanceArrived(
  read: () => Promise<bigint>,
  need: bigint,
  waitMs: number,
  opts: { sleep?: (ms: number) => Promise<void>; now?: () => number; everyMs?: number } = {}
): Promise<boolean> {
  const sleep = opts.sleep || ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = opts.now || (() => Date.now());
  const every = opts.everyMs ?? 2500;
  const deadline = now() + Math.max(0, waitMs);
  for (;;) {
    try {
      if ((await read()) >= need) return true;
    } catch {
      /* a failed read is not an answer; try again until the deadline */
    }
    if (now() + every > deadline) return false;
    await sleep(every);
  }
}

/** How long to watch the allowance after the approve request failed: none after a plain "no". */
export function lateApproveWaitMs(errorCode: number | undefined): number {
  return errorCode === 4001 ? 0 : 20_000;
}


/**
 * Error codes for "the allowance is too low". The server sends `allowance_low`; older records and the
 * kekflix docs use `insufficient_allowance`.
 */
const ALLOWANCE_ERROR_CODES = ["allowance_low", "insufficient_allowance"];

export function isAllowanceError(code: string | null | undefined): boolean {
  return ALLOWANCE_ERROR_CODES.includes(String(code || ""));
}

/**
 * Whether to offer "Renew approval". A charge refused for allowance always offers it: the wallet's
 * allowance is shared, so it can cover this subscription's period limit and still be short once the
 * other subscriptions take their share. Otherwise offer it when the allowance on-chain is below the limit.
 */
export function shouldOfferRenew(lastErrorCode: string | null | undefined, allowance: bigint | null, needed: bigint): boolean {
  if (isAllowanceError(lastErrorCode)) return true;
  return allowance !== null && needed > 0n && allowance < needed;
}
